import { Textarea, TextareaProps } from 'baseui/textarea';
import { themedStyled } from 'common/styles/styletron.theme';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import { styles } from '../styles';

export interface TextAreaInputProps
  extends BaseElementInputProps,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    >,
    Omit<TextareaProps, 'name'> {
  type: 'text-area';
  fieldType?: 'control' | 'uncontrol';
}

export default function TextAreaInput(props: TextAreaInputProps) {
  const { control } = useFormContext();
  const context = React.useContext(FormContext);
  const watchValue = useWatch({ control, name: props.name });
  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ name: props.name });

  const {
    readOnly,
    required,
    hideLabel,
    label = '',
    fieldType = 'control',
    ...restProps
  } = props;
  const disabled = !context.editable || restProps.disabled;

  if (readOnly) {
    return (
      <CustomFormControl
        required={required}
        hideLabel={hideLabel}
        label={label}
      >
        <StyledDiv>
          <StyledTextArea readOnly inputMode="text" value={watchValue} />
        </StyledDiv>
      </CustomFormControl>
    );
  }
  const shouldShowError = !!fieldState.error?.message;

  return (
    <CustomFormControl
      required={required}
      hideLabel={hideLabel}
      label={label}
      error={fieldState.error?.message}
      disabled={disabled}
    >
      <Textarea
        //@ts-ignore
        inputRef={ref}
        {...field}
        {...restProps}
        {...(fieldType === 'uncontrol' && {
          value: watchValue,
        })}
        overrides={{
          Root: { style: { ...(disabled ? styles.reset : styles.active) } },
          Input: {
            style: {
              paddingTop: '2px',
              paddingBottom: '2px',
              ...(disabled ? styles.reset : styles.active),
            },
          },
          InputContainer: {
            style: { ...(disabled ? styles.reset : styles.active) },
          },
          ...props.overrides,
        }}
        disabled={disabled}
        rows={3}
        error={shouldShowError}
        clearOnEscape
      />
    </CustomFormControl>
  );
}

export const StyledDiv = themedStyled('div', ({ $theme }) => {
  return {
    background: $theme.colors.primary100,
    height: '96px',
    width: '100%',
    border: `2px solid ${$theme.colors.primary100}`,
    outline: 'none',
  };
});

export const StyledTextArea = themedStyled('textarea', ({ $theme }) => {
  return {
    background: $theme.colors.primary100,
    padding: '10px 14px',
    minHeight: '92px',
    width: '100%',
    lineHeight: '24px',
    fontSize: '16px',
    outline: 'none',
    border: 'none',
    resize: 'none',
  };
});
